import { useEffect, useState } from "react";
import {
  Account,
  Invoice,
  ZuoraPaymentMethod,
  Location,
  InvoicingSystemTypes,
} from "@/api/opb/opbTypes";
import { opbApi } from "@/api/opb/opbApi";

export default function useAccount(
  accountId?: string,
  params?: {
    withInvoices?: boolean;
    withBalance?: boolean;
    withPaymentMethods?: boolean;
  }
) {
  const {
    withInvoices,
    withBalance = false,
    withPaymentMethods,
  } = params ?? {};

  const [isLoadingAccount, setIsLoadingAccount] = useState(true);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(withInvoices);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] =
    useState(withPaymentMethods);

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [account, setAccount] = useState<Account>();
  const [location, setLocation] = useState<Location>();
  const [zuoraPaymentMethods, setZuoraPaymentMethods] = useState<
    ZuoraPaymentMethod[]
  >([]);
  const [stripePaymentMethods, setStripePaymentMethods] = useState<any[]>([]);

  const isStripeBilling =
    account?.invoicingSystemType === InvoicingSystemTypes.StripeBilling;

  const fetchUserAccounts = async () => {
    if (!accountId) return;
    try {
      setIsLoadingAccount(true);
      const accountResponse = await opbApi.fetchAccount(accountId);
      setAccount(accountResponse);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsLoadingAccount(false);
    }
  };

  const fetchLocation = async () => {
    if (!account?.locationId) return;
    try {
      const opbLocation = await opbApi.fetchLocation(account.locationId);
      setLocation(opbLocation);
    } finally {
      setIsLoadingLocation(false);
    }
  };
  const fetchInvoices = async () => {
    if (!accountId || !account) return;
    try {
      setIsLoadingInvoices(true);
      const invoiceResponse = await opbApi.fetchAccountInvoices(accountId, {
        withBalance,
        isStripeBilling,
      });
      setInvoices(invoiceResponse);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  const fetchPaymentMethods = async () => {
    if (!accountId) return;
    try {
      setIsLoadingPaymentMethods(true);
      if (isStripeBilling) {
        const stripeResponse = await opbApi.fetchStripePaymentMethods(
          accountId
        );
        setStripePaymentMethods(stripeResponse.data);
      } else {
        const zuoraResponse = await opbApi.fetchPaymentMethods(accountId);
        setZuoraPaymentMethods(zuoraResponse.data);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsLoadingPaymentMethods(false);
    }
  };
  useEffect(() => {
    if (withPaymentMethods) {
      fetchPaymentMethods();
    }
  }, [accountId, withPaymentMethods]);

  useEffect(() => {
    if (withInvoices) {
      fetchInvoices();
    }
  }, [account, withInvoices]);

  useEffect(() => {
    if (!account?.locationId) return;
    if (location) return;
    fetchLocation();
  }, [account?.locationId]);

  useEffect(() => {
    if (!accountId) return;
    fetchUserAccounts();
  }, [accountId]);

  // eslint-disable-next-line no-nested-ternary
  const name = isLoadingLocation
    ? ""
    : location
    ? `${location.city} — ${location.name}`
    : account?.globalAccountName ?? "";

  return {
    isLoadingAccount,
    isLoadingLocation,
    account,
    location,
    name,
    invoices,
    isLoadingInvoices,
    isLoading:
      isLoadingInvoices ||
      isLoadingAccount ||
      isLoadingLocation ||
      isLoadingPaymentMethods,
    isLoadingPaymentMethods,
    zuoraPaymentMethods,
    stripePaymentMethods,
  };
}
